// src/utils/logUtils.ts

export interface ChangeLog {
  id: number;
  entity: string;
  vtr_id: string;
  column_name: string;
  reason_for_change?: string;
  old_value: string | null;
  new_value: string | null;
  username: string;
  user_id: string;
  timestamp: string;
  update_method?: string;
}

/**
 * Utility to extract date (YYYY-MM-DD) from timestamp string.
 */
export function getDateKey(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toISOString().split('T')[0]; // e.g. "2025-09-04"
}
function isNoChange(oldVal: any, newVal: any): boolean {
  const normalize = (v: any) =>
    (v === null || v === undefined || v === "") ? "" : String(v).trim();

  return normalize(oldVal) === normalize(newVal);
}
export function groupAndSortLogs(logs: ChangeLog[]): {
  flattened: (ChangeLog & { groupDate: string; time: string })[];
  dateKeys: string[];
} {
  // 1️⃣ Filter out entries with no actual change
  const filteredLogs = logs.filter(log => !isNoChange(log.old_value, log.new_value));

  // 2️⃣ Group by date
  const grouped = filteredLogs.reduce((acc, log) => {
    const date = getDateKey(log.timestamp);
    if (!acc[date]) acc[date] = [];
    acc[date].push(log);
    return acc;
  }, {} as Record<string, ChangeLog[]>);

  // 3️⃣ Sort date keys (latest → oldest)
  const dateKeys = Object.keys(grouped).sort((a, b) => (a < b ? 1 : -1));

  // 4️⃣ Flatten & format
  const flattened = dateKeys.flatMap(date =>
    grouped[date]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .map((item, idx) => {
        const time = new Date(item.timestamp).toISOString().split('T')[1].split('.')[0];

        const formattedUsername = item.username?.includes('@')
          ? item.username.split('@')[0]
          : item.username;

        return {
          ...item,
          username: formattedUsername,
          groupDate: idx === 0 ? date : '',
          time,
        };
      })
  );

  return { flattened, dateKeys };
}

export function filterLogs(logs: ChangeLog[], userFilter: string, columnFilter: string) {
  return logs.filter(
    item =>
      item.user_id.toLowerCase().includes(userFilter.toLowerCase()) &&
      item.column_name.toLowerCase().includes(columnFilter.toLowerCase())
  );
}
